import React from 'react';
import Button from '@material-ui/core/Button';
import CardActions from '@material-ui/core/CardActions';
import {
  ArrayInput,
  DateInput,
  DisabledInput,
  Edit,
  FormDataConsumer,
  SaveButton,
  SimpleForm,
  SimpleFormIterator,
  Toolbar
} from 'react-admin';
import RichTextInput from 'ra-input-rich-text';
import { buildTracking, TrackingTimeline } from '@shared/tracking-timeline';
import FieldGroup from '~components/FieldGroup';
import { parsePosition } from '~utils/trackingPosition';
import DeleteTrackingButton from '../DeleteTrackingButton';
import { AutoProgressField, CurrentPositionInput, DerivedProgressSync } from './positionInputs';
import styles from './styles.module.scss';

const FIELD = 'resources.project-tracking.fields';
const toolbarOptions = [['bold', 'italic', 'underline', 'link'], [{ list: 'bullet' }]];

const projectShowPath = code => `/projects/${encodeURIComponent(code)}/show`;

const isPercentage = value => {
  const number = Number(value);
  return value !== '' && value !== null && value !== undefined && Number.isInteger(number) && number >= 0 && number <= 100;
};

// Mirrors the API rules so the form explains a rejection before it is sent. Per-etapa
// progress is derived from the position (kept in stages[i].progress by DerivedProgressSync).
const validateTracking = values => {
  const errors = {};
  if (!values.position) errors.position = 'Seleccione la sub-etapa actual';
  if (!isPercentage(values.currentProgress)) errors.currentProgress = 'Debe ser un número entero entre 0 y 100';
  const stages = values.stages || [];
  const stageErrors = stages.map(() => ({}));
  stages.forEach((stage, index) => {
    if (Number(stage.progress) > 0 && !stage.startDate) {
      stageErrors[index].startDate = 'Obligatoria cuando la etapa tiene avance';
      // redux-form only shows errors of touched fields, and the date may never have been
      // touched: repeat it on the (always visible) progress cell.
      stageErrors[index].progress = 'Indique la fecha de inicio de la etapa';
    }
    if (stage.startDate && stage.expectedEndDate && stage.expectedEndDate < stage.startDate) {
      stageErrors[index].expectedEndDate = 'No puede ser anterior a la fecha de inicio';
    }
  });
  if (stageErrors.some(stageError => Object.keys(stageError).length)) errors.stages = stageErrors;
  return errors;
};

const TrackingTitle = ({ record }) => <span>Seguimiento del proyecto {record ? record.id : ''}</span>;

const TrackingActions = ({ data, history }) => (
  <CardActions>
    {data && (
      <Button color="primary" onClick={() => history.push(projectShowPath(data.id))}>
        Volver al proyecto
      </Button>
    )}
  </CardActions>
);

const TrackingToolbar = props => (
  <Toolbar {...props} style={{ display: 'flex', justifyContent: 'space-between' }}>
    <SaveButton />
    <DeleteTrackingButton record={props.record} />
  </Toolbar>
);

// The public site's timeline (shared component), redrawn from the unsaved form values.
const TrackingPreview = () => (
  <FormDataConsumer>
    {({ formData }) => (
      <div className={styles.preview}>
        <p className={styles.previewTitle}>Vista previa (así se verá en la ficha pública)</p>
        <TrackingTimeline
          tracking={buildTracking((formData && formData.stages) || [], {
            ...parsePosition(formData && formData.position),
            currentProgress: formData && formData.currentProgress
          })}
        />
      </div>
    )}
  </FormDataConsumer>
);

// A component (not a bare <p>): FieldGroup passes form props to its children.
const GridHint = () => <p className={styles.gridHint}>El avance de cada etapa se calcula automáticamente.</p>;

const RecordedLastUpdate = ({ record }) => (
  <p>
    {record && record.hasTracking
      ? `Última actualización registrada: ${record.recordedLastUpdate}`
      : 'Este proyecto todavía no tiene seguimiento. Los avances se completaron a partir de su etapa actual.'}
  </p>
);

function ProjectTrackingEdit(props) {
  return (
    <Edit
      undoable={false}
      title={<TrackingTitle />}
      actions={<TrackingActions history={props.history} />}
      {...props}
    >
      <SimpleForm
        toolbar={<TrackingToolbar />}
        validate={validateTracking}
        redirect={(basePath, id) => projectShowPath(id)}
      >
        <FieldGroup label="fieldGroups.trackingStatus">
          <RecordedLastUpdate />
          <DateInput source="lastUpdate" label={`${FIELD}.lastUpdate`} />
          <RichTextInput source="description" label={`${FIELD}.description`} toolbar={toolbarOptions} />
        </FieldGroup>
        <FieldGroup label="fieldGroups.trackingStages">
          <TrackingPreview />
          <CurrentPositionInput />
          <DerivedProgressSync />
          <ArrayInput source="stages" label="">
            <SimpleFormIterator disableAdd disableRemove classes={{ form: styles.stageRow, line: styles.stageLine }}>
              <DisabledInput source="name" label={`${FIELD}.stage`} fullWidth />
              <DisabledInput source="weight" label={`${FIELD}.weight`} fullWidth />
              <AutoProgressField source="progress" label="Avance" />
              <DateInput source="startDate" label={`${FIELD}.startDate`} fullWidth />
              <DateInput source="expectedEndDate" label={`${FIELD}.expectedEndDate`} fullWidth />
            </SimpleFormIterator>
          </ArrayInput>
          <GridHint />
        </FieldGroup>
      </SimpleForm>
    </Edit>
  );
}

export default ProjectTrackingEdit;
